import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  ArrowLeft,
  MessageSquare,
  Clock,
  User,
  Calendar,
  Search,
  Loader2,
  AlertTriangle,
  Smile,
  Frown,
  Meh,
  ClipboardCheck,
  Brain,
  Download,
  AlertCircle,
} from "lucide-react";

const ChatDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authAxios } = useAuth();

  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [chatComments, setChatComments] = useState("");
  const [savingComments, setSavingComments] = useState(false);
  const transcriptRef = useRef(null);

  const fetchChat = async () => {
    try {
      const response = await authAxios.get(`/chats/${id}`);
      setChat(response.data);
      setChatComments(response.data.comments || "");
    } catch (error) {
      console.error("Error fetching chat detail:", error);
      toast.error("Error al cargar detalles del chat");
      navigate("/chats");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveComments = async () => {
    setSavingComments(true);
    try {
      await authAxios.post(`/chats/${id}/comment`, { comment: chatComments });
      toast.success("Comentario guardado exitosamente");
      fetchChat();
    } catch (error) {
      console.error("Error saving comments:", error);
      toast.error("Error al guardar comentario");
    } finally {
      setSavingComments(false);
    }
  };

  useEffect(() => {
    fetchChat();
  }, [id]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      await authAxios.post(`/chats/${id}/analyze`);
      toast.success("Análisis completado exitosamente");
      fetchChat();
    } catch (error) {
      console.error("Error analyzing chat:", error);
      toast.error("Error al ejecutar análisis de chat");
    } finally {
      setAnalyzing(false);
    }
  };

  const getSentimentIcon = (sentiment) => {
    if (sentiment === undefined || sentiment === null) return <Meh className="w-5 h-5 text-slate-400" />;
    const val = parseFloat(sentiment);
    if (val >= 7.5) return <Smile className="w-5 h-5 text-emerald-500" />;
    if (val >= 5.0) return <Meh className="w-5 h-5 text-amber-500" />;
    return <Frown className="w-5 h-5 text-rose-500" />;
  };

  const getSentimentBadgeClass = (sentiment) => {
    if (sentiment === undefined || sentiment === null) return "bg-slate-100 text-slate-600";
    const val = parseFloat(sentiment);
    if (val >= 7.5) return "bg-emerald-100 text-emerald-700";
    if (val >= 5.0) return "bg-amber-100 text-amber-700";
    return "bg-rose-100 text-rose-700";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-10 h-10 text-emerald-600 animate-spin" />
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Conversación de chat no encontrada</p>
        <Button onClick={() => navigate("/chats")} className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white">
          Volver a chats
        </Button>
      </div>
    );
  }

  // Parse messages JSON
  let messages = [];
  const rawMessages = chat.messages_data || chat.messages;
  try {
    if (typeof rawMessages === "string") {
      messages = JSON.parse(rawMessages);
    } else if (Array.isArray(rawMessages)) {
      messages = rawMessages;
    }
  } catch (e) {
    console.error("Error parsing messages:", e);
  }
  const filteredMessages = messages.filter((msg) =>
    searchTerm === "" || (msg.message || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sentiment = chat.sentiment_analysis || {};

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("/chats")}
            className="hover:bg-slate-100"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-800 border-none">WhatsApp</Badge>
              <h1 className="text-2xl font-bold text-slate-900 truncate max-w-[200px] md:max-w-md lg:max-w-xl">
                {chat.callerid || "Chat sin número"}
              </h1>
            </div>
            <p className="text-xs text-slate-500 font-mono mt-1">GUID: {chat.guid}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={handleAnalyze}
            disabled={analyzing}
            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-500/10 flex items-center gap-2"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Analizando...
              </>
            ) : (
              <>
                <Brain className="w-4 h-4" />
                Re-analizar con IA
              </>
            )}
          </Button>
        </div>
      </div>

      {/* KPI Cards (Matches Calls Page structure) */}
      {chat.is_analyzed && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Sentimiento General</p>
              <p className="text-3xl font-bold text-sky-600">{(chat.sentiment_score || 5.0).toFixed(1)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Mensajes</p>
              <p className="text-3xl font-bold text-slate-700">{messages.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Riesgo</p>
              <p className={`text-3xl font-bold ${chat.risk_score >= 50 ? 'text-red-600' : 'text-green-600'}`}>
                {(chat.risk_score || 0).toFixed(0)}%
              </p>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Satisfacción (NPS)</p>
              <p className="text-3xl font-bold text-slate-700">{(sentiment.nps_score || 5.0).toFixed(1)}</p>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">FCR</p>
              <p className="text-3xl font-bold text-slate-700">{chat.auto_insights?.fcr || "N/A"}</p>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 mb-1">Adherencia</p>
              <p className="text-3xl font-bold text-slate-700">{chat.auto_insights?.adherence || "N/A"}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Grid Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Chat Transcript */}
        <div className="lg:col-span-2 space-y-6">
          {/* Chat Transcript / Conversation View */}
          <Card className="border-slate-100 shadow-sm overflow-hidden flex flex-col h-[650px]">
            <CardHeader className="border-b border-slate-100 p-4 bg-slate-50/50 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-emerald-600" />
                Transcripción del Chat ({filteredMessages.length} mensajes)
              </CardTitle>
              <div className="relative w-64">
                <Input
                  placeholder="Buscar en el chat..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 h-8 text-xs"
                />
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
              </div>
            </CardHeader>
            <CardContent
              ref={transcriptRef}
              className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/20"
            >
              {filteredMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <AlertCircle className="w-8 h-8 mb-2" />
                  <p className="text-sm">No se encontraron mensajes en esta conversación.</p>
                </div>
              ) : (
                filteredMessages.map((msg, index) => {
                  const senderType = msg.sender_type || (
                    msg.speaker?.includes("CLIENTE") ? "client" :
                    msg.speaker?.includes("AGENTE") ? "agent" :
                    String(msg.direction || "").toLowerCase() === "i" ? "client" :
                    String(msg.direction || "").toLowerCase() === "o" ? "agent" :
                    "unknown"
                  );
                  const isClient = senderType === "client";
                  const isUnknown = senderType === "unknown";

                  // Determine display label
                  const speakerLabel = msg.speaker ||
                    (isClient ? "CLIENTE" : msg.agent ? `AGENTE ${msg.agent}` : "AGENTE");

                  return (
                    <div
                      key={index}
                      className={`flex flex-col ${isClient ? "items-start" : isUnknown ? "items-center" : "items-end"}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-2xl px-4 py-2.5 shadow-sm text-sm ${
                          isClient
                            ? "bg-white text-slate-900 border border-slate-100 rounded-tl-none"
                            : isUnknown
                            ? "bg-amber-50 text-amber-900 border border-amber-100"
                            : "bg-emerald-600 text-white rounded-tr-none"
                        }`}
                      >
                        {/* Speaker Indicator */}
                        <div className="flex items-center gap-1.5 mb-1">
                          <User className={`w-3.5 h-3.5 ${isClient ? "text-slate-400" : isUnknown ? "text-amber-500" : "text-emerald-200"}`} />
                          <span className={`text-[10px] font-bold tracking-wide uppercase ${
                            isClient ? "text-slate-500" : isUnknown ? "text-amber-600" : "text-emerald-100"
                          }`}>
                            {speakerLabel}
                          </span>
                        </div>

                        {/* Content text */}
                        <p className="whitespace-pre-wrap break-words">{msg.message || ""}</p>

                        {/* Attachments */}
                        {msg.attachments && (
                          <div className={`mt-2 p-2 rounded-lg text-xs ${
                            isClient ? "bg-slate-50 border border-slate-100" : "bg-emerald-700 text-emerald-100"
                          }`}>
                            <span className="font-semibold block mb-1">📁 Archivo Adjunto:</span>
                            <a
                              href={typeof msg.attachments === "string" ? msg.attachments : (msg.attachments.url || "#")}
                              target="_blank"
                              rel="noreferrer"
                              className="underline flex items-center gap-1 hover:text-emerald-300"
                            >
                              Ver Archivo
                            </a>
                          </div>
                        )}
                      </div>
                      {/* Timestamp */}
                      <span className="text-[10px] text-slate-400 mt-1 px-2">
                        {msg.time || ""}
                      </span>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column: Info & AI Insights Sidebar */}
        <div className="space-y-6">
          {/* Executive Summary (Moves to right sidebar like in calls) */}
          {chat.is_analyzed && chat.summary && (
            <Card className="border-slate-100 shadow-sm overflow-hidden">
              <CardHeader className="pb-2 bg-slate-50/50 border-b border-slate-100">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Brain className="w-5 h-5 text-emerald-600" />
                  <span>Resumen Ejecutivo</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <p className="text-slate-600 leading-relaxed text-sm">{chat.summary}</p>
              </CardContent>
            </Card>
          )}

          {/* Topics (Matches Calls topics visual structure) */}
          {chat.is_analyzed && chat.detected_topics && chat.detected_topics.length > 0 && (
            <Card>
              <CardHeader className="pb-2 bg-slate-50/50 border-b border-slate-100">
                <CardTitle className="text-base font-semibold">Temas Detectados</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-2">
                  {chat.detected_topics.map((topic, i) => (
                    <Badge key={i} className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-none">
                      {topic}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Keywords / Palabras clave */}
          {chat.is_analyzed && sentiment.keywords && sentiment.keywords.length > 0 && (
            <Card>
              <CardHeader className="pb-2 bg-slate-50/50 border-b border-slate-100">
                <CardTitle className="text-base font-semibold">Palabras Clave</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-2">
                  {sentiment.keywords.map((kw, i) => (
                    <Badge key={i} variant="outline" className="font-normal">
                      {kw.word} ({kw.count})
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Risk Factors / Factores de Riesgo */}
          {chat.is_analyzed && sentiment.risk_factors && sentiment.risk_factors.length > 0 && (
            <Card className="border-orange-200 bg-orange-50/30">
              <CardHeader className="pb-2 border-b border-orange-100 bg-orange-50/60">
                <CardTitle className="text-base font-semibold flex items-center gap-2 text-orange-700">
                  <AlertTriangle className="w-5 h-5" />
                  Factores de Riesgo
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <ul className="space-y-2">
                  {sentiment.risk_factors.map((factor, i) => (
                    <li key={i} className="flex items-start gap-2 text-orange-700 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-2 flex-shrink-0" />
                      {factor}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Supervisor Feedback (Retroalimentación de Supervisor) */}
          {chat.manual_evaluation && (
            <Card className="border-purple-200 bg-purple-50/30" data-testid="supervisor-evaluation">
              <CardHeader className="pb-2 border-b border-purple-100 bg-purple-50/60">
                <CardTitle className="text-base font-semibold flex items-center gap-2 text-purple-800">
                  <User className="w-5 h-5" />
                  Retroalimentación del Supervisor
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="text-center p-4 bg-white rounded-lg border border-purple-100">
                  <p className="text-xs text-slate-500 font-medium">Nota de Calidad Manual</p>
                  <p className={`text-4xl font-extrabold ${
                    chat.manual_evaluation.score >= 80 ? 'text-green-600' :
                    chat.manual_evaluation.score >= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {chat.manual_evaluation.score}/100
                  </p>
                </div>

                <div className="bg-white rounded-lg p-3 border border-purple-50 space-y-1">
                  <p className="text-xs text-slate-400 block font-medium">Comentarios y Oportunidades de Mejora</p>
                  <p className="text-sm text-slate-700 italic">"{chat.manual_evaluation.comments || 'Sin comentarios adicionales.'}"</p>
                </div>

                <div className="flex justify-between text-xs text-slate-500 px-1 pt-1">
                  <span>Evaluador: <strong>{chat.manual_evaluation.evaluator_name}</strong></span>
                  {chat.manual_evaluation.evaluated_at && (
                    <span>Fecha: {format(new Date(chat.manual_evaluation.evaluated_at), "dd/MM/yyyy", { locale: es })}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Details of the record */}
          <Card className="border-slate-100 shadow-sm">
            <CardHeader className="p-4 border-b border-slate-100 bg-slate-50/50">
              <CardTitle className="text-sm font-semibold">Detalles del Registro</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="space-y-1">
                <span className="text-xs text-slate-400 block font-medium">Cliente</span>
                <span className="text-sm font-semibold text-slate-800">{chat.callerid || "N/A"}</span>
              </div>
              <Separator className="bg-slate-100" />

              <div className="space-y-1">
                <span className="text-xs text-slate-400 block font-medium">Agente</span>
                <span className="text-sm font-semibold text-slate-800">{chat.agent_name || chat.agent || "N/A"}</span>
              </div>
              <Separator className="bg-slate-100" />

              <div className="space-y-1">
                <span className="text-xs text-slate-400 block font-medium">Campaña</span>
                <span className="text-sm font-semibold text-slate-800">{chat.campaign || "N/A"}</span>
              </div>
              <Separator className="bg-slate-100" />

              <div className="space-y-1">
                <span className="text-xs text-slate-400 block font-medium">Fecha</span>
                <span className="text-sm font-semibold text-slate-800">
                  {chat.chat_date ? format(new Date(chat.chat_date), "dd/MM/yyyy HH:mm:ss") : "N/A"}
                </span>
              </div>
              <Separator className="bg-slate-100" />

              <div className="space-y-2">
                <span className="text-xs text-slate-400 block font-medium">Estado de Análisis</span>
                {chat.is_analyzed ? (
                  <div className="flex items-center gap-1.5 text-emerald-600 text-xs font-semibold">
                    <ClipboardCheck className="w-4 h-4" />
                    Analizado exitosamente
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-slate-500 text-xs font-semibold">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Pendiente de análisis
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Chat Comments / Notas de Falla */}
          <Card className="border-slate-100 shadow-sm">
            <CardHeader className="p-4 border-b border-slate-100 bg-slate-50/50">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-emerald-600 animate-pulse" />
                Notas y Comentarios (Fallas o Seguimiento)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <textarea
                className="w-full min-h-[100px] border border-slate-200 rounded-md p-3 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-slate-50/30"
                placeholder="Escribe aquí si hubo alguna falla en el proceso, plataforma o dudas operativas sobre este chat..."
                value={chatComments}
                onChange={(e) => setChatComments(e.target.value)}
              />
              <div className="flex justify-end">
                <Button 
                  size="sm" 
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-xs"
                  onClick={handleSaveComments}
                  disabled={savingComments}
                >
                  {savingComments ? "Guardando..." : "Guardar Nota"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* General Sentiment */}
          {chat.is_analyzed && (
            <Card className="border-slate-100 shadow-sm">
              <CardHeader className="p-4 border-b border-slate-100 bg-slate-50/50">
                <CardTitle className="text-sm font-semibold">Sentimiento General</CardTitle>
              </CardHeader>
              <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                <div className={`p-4 rounded-full mb-3 ${getSentimentBadgeClass(chat.sentiment_score)}`}>
                  {getSentimentIcon(chat.sentiment_score)}
                </div>
                <h3 className="text-lg font-bold text-slate-800">
                  {chat.sentiment_score >= 7.5
                    ? "Excelente"
                    : chat.sentiment_score >= 5.0
                    ? "Neutral"
                    : "Crítico"}
                </h3>
                <span className="text-xs text-slate-400 font-mono mt-1">Score: {chat.sentiment_score} / 10</span>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatDetailPage;
